import { useState } from "react";
import { Mail, Check } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Compact newsletter signup form. Used in the Footer and on /newsletter.
 * Props:
 *   - source: string identifying where the signup happened (footer, newsletter-page, etc.)
 *   - variant: "compact" (default, single row) | "large" (stacked, hero-sized)
 */
export default function NewsletterSignup({ source = "footer", variant = "compact" }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email: email.trim(), source });
      setDone(true);
      if (data.reactivated) {
        toast.success("Welcome back — you're re-subscribed.");
      } else {
        toast.success("You're on the list. Check your inbox.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't subscribe. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className={`inline-flex items-center gap-2 ${variant === "large" ? "text-base" : "text-sm"} text-green-400`} data-testid="newsletter-signup-done">
        <Check className="h-4 w-4" />
        You're on the list.
      </div>
    );
  }

  if (variant === "large") {
    return (
      <form onSubmit={submit} className="w-full max-w-lg flex flex-col sm:flex-row gap-2" data-testid={`newsletter-signup-${source}`}>
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 pointer-events-none" />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            disabled={busy}
            className="input-dark pl-10 w-full"
            data-testid="newsletter-email-input"
          />
        </div>
        <button type="submit" disabled={busy || !email.trim()} className="ember-btn disabled:opacity-40 sm:w-auto" data-testid="newsletter-submit">
          {busy ? "Sending..." : "Subscribe"}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={submit} className="w-full max-w-sm flex gap-2" data-testid={`newsletter-signup-${source}`}>
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        disabled={busy}
        className="input-dark flex-1 !py-2 text-sm"
        data-testid="newsletter-email-input"
      />
      <button type="submit" disabled={busy || !email.trim()} className="ghost-btn !py-2 !px-4 text-sm hover:border-[#E65F2B]/60 hover:text-[#E65F2B] disabled:opacity-40" data-testid="newsletter-submit">
        {busy ? "..." : "Subscribe"}
      </button>
    </form>
  );
}
